import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import { handleError } from '../utils'

const FETCH_REQUEST = 'contenthouse/emailReplacement/FETCH_REQUEST'
const FETCH_SUCCESS = 'contenthouse/emailReplacement/FETCH_SUCCESS'
const FETCH_FAILURE = 'contenthouse/emailReplacement/FETCH_FAILURE'

const CREATE_REQUEST = 'contenthouse/emailReplacement/CREATE_REQUEST'
const CREATE_SUCCESS = 'contenthouse/emailReplacement/CREATE_SUCCESS'
const CREATE_FAILURE = 'contenthouse/emailReplacement/CREATE_FAILURE'

const UPDATE_REQUEST = 'contenthouse/emailReplacement/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'contenthouse/emailReplacement/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'contenthouse/emailReplacement/UPDATE_FAILURE'

const DELETE_REQUEST = 'contenthouse/emailReplacement/DELETE_REQUEST'
const DELETE_SUCCESS = 'contenthouse/emailReplacement/DELETE_SUCCESS'
const DELETE_FAILURE = 'contenthouse/emailReplacement/DELETE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(){
  return {
    type: FETCH_REQUEST,
  }
}

export function fetchSuccess(){
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(){
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadEmailReplacements(){
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api('/v1/email-replacements')
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.EMAIL_REPLACEMENT_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(data))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function createEmailReplacement(emailReplacement, options = {}){
  const config = {
    method: 'POST',
    data: emailReplacement,
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api('/v1/email-replacements', options, config)
      .then((data) => {
        const addedEmailReplacement = { id: data.data, ...emailReplacement }
        const normalizedJson = normalize(addedEmailReplacement, Schemas.EMAIL_REPLACEMENT)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const validationError = JSON.parse(data.response.request.responseText).errors.Input ?? JSON.parse(data.response.request.responseText).errors.Output ?? null
        const errors = validationError ?? handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function updateEmailReplacement(emailReplacement, options = {}){
  const config = {
    method: 'PUT',
    data: emailReplacement,
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/v1/email-replacements/${emailReplacement.id}`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(emailReplacement, Schemas.EMAIL_REPLACEMENT)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())
        return { success: true, data }
      })
      .catch((data) => {
        const validationError = JSON.parse(data.response.request.responseText).errors.Input ?? JSON.parse(data.response.request.responseText).errors.Output ?? null
        const errors = validationError ?? handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function deleteEmailReplacement(emailReplacement){
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/v1/email-replacements/${emailReplacement.id}`, { permanent: true }, config)
      .then(({ data }) => {
        const normalizedJson = normalize(emailReplacement, Schemas.EMAIL_REPLACEMENT)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false, result: errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
